/**
 * A ponte: desenha um campo do `ScreenDocument` reusando o `webhook-common`.
 *
 * UM renderer para os 16 tipos de campo que o `webhook-common` já sabe desenhar
 * — no lugar do adaptador de 1.213 linhas, dos três conversores no service e das
 * ~15 referências espalhadas no HTML do canvas.
 *
 * O custo, dito por inteiro: isto puxa o `WebhookCreatorModule` para o bundle de
 * quem renderiza tela. É a troca deliberada da seção 4.5 — entregar tela
 * funcionando agora, com 18 widgets já testados em produção, em vez de reescrever
 * 19 widgets antes de entregar qualquer coisa. Trocar um deles por widget nativo
 * é mudar UMA linha no mapa de renderers, e o rollback é reverter essa linha.
 */
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  ViewContainerRef,
  ChangeDetectorRef,
  Component as AngularComponent,
  computed,
  effect,
  inject,
  input,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { ScreenNode } from '../../model/node-props';
import { SCREEN_RUNTIME } from '../../runtime/screen-runtime';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
// SEM import estatico do `WebhookCreatorModule`: ele chega por `import()` no
// primeiro campo desenhado. Ver o comentario do metodo `mount`.
import type {
  WebhookConfiguration,
  WebhookField
} from '../../../webhook-creator/webhook-creator/webhook-interfaces/configuration';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { nodeToWebhookField, withTranslatedText } from './node-to-webhook-field';

/**
 * Configuração vazia.
 *
 * O `webhook-common` repassa `configuration` para os filhos, que a usam para
 * resolver referência entre campos do MESMO webhook. Numa tela do construtor não
 * há webhook nenhum, e as referências entre campos passam pelo `RuleEvaluator`.
 * Uma constante compartilhada evita alocar um objeto por campo por ciclo de
 * detecção.
 */
const EMPTY_CONFIGURATION: WebhookConfiguration = { tabs: [], panels: [], fields: [] };

@Component({
  selector: 'tem-webhook-field',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // O componente do webhook e montado AQUI dentro, por codigo, depois de o
  // modulo dele chegar. O `ng-container` e so o ponto de ancoragem.
  template: `<ng-container #slot />`
})
export class WebhookFieldRenderer implements NodeRenderer {

  readonly node = input.required<ScreenNode>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly configuration = EMPTY_CONFIGURATION;

  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly slot = viewChild.required('slot', { read: ViewContainerRef });

  /** O componente do webhook, depois de montado. */
  private mounted: ComponentRef<Record<string, unknown>> | null = null;

  /** Evita duas cargas simultaneas quando varios campos aparecem de uma vez. */
  private mounting = false;

  constructor() {
    // Monta na primeira vez que ha o que desenhar, e atualiza as entradas nas
    // vezes seguintes. `effect` porque as entradas sao signals: o campo muda
    // quando o no muda, e o formulario chega depois da primeira renderizacao.
    effect(() => {
      const form = this.form();
      const field = this.field();

      if (form === null || field === null) {
        return;
      }

      if (this.mounted !== null) {
        this.applyInputs(this.mounted, form, field);
        return;
      }

      void this.mount(form, field);
    });

    this.destroyRef.onDestroy(() => this.mounted?.destroy());
  }

  /**
   * Traz o modulo do webhook e monta o campo.
   *
   * `import()` dinamico: e o que tira o modulo do caminho estatico de render.
   * Carregar o NgModule antes de criar o componente e obrigatorio — o
   * `webhook-common` e `standalone: false`, e `createComponent` so sabe monta-lo
   * depois que o escopo do modulo dele foi registrado.
   */
  private async mount(form: FormGroup, field: WebhookField): Promise<void> {
    if (this.mounting) {
      return;
    }

    this.mounting = true;

    try {
      // A ordem importa: o modulo primeiro, o componente depois.
      await import('../../../webhook-creator/webhook-creator.module');
      const modulo = await import(
        '../../../webhook-creator/webhook-creator/webhook-form-components/common.components'
      );

      const tipo = modulo.WebhookCommonComponent as never;
      const slot = this.slot();

      slot.clear();

      const criado = slot.createComponent<Record<string, unknown>>(tipo);
      this.applyInputs(criado, form, field);

      this.mounted = criado;
      this.cdr.markForCheck();
    } finally {
      this.mounting = false;
    }
  }

  /**
   * Passa as entradas para o componente montado.
   *
   * `setInput` e nao atribuicao direta: e o que dispara o `ngOnChanges` do
   * `webhook-common`, que e como ele reage a troca de campo. Atribuir na
   * instancia mudaria o valor sem avisar o componente.
   */
  private applyInputs(
    ref: ComponentRef<Record<string, unknown>>,
    form: FormGroup,
    field: WebhookField
  ): void {

    ref.setInput('mainformGroup', form);
    ref.setInput('rootForm', form);
    ref.setInput('field', field);
    ref.setInput('fieldNew', this.controlName());
    ref.setInput('configuration', this.configuration);
    ref.setInput('variables', []);
    ref.setInput('n', 0);
  }

  /** Nome do controle no formulário plano montado por `ScreenFormService`. */
  readonly controlName = computed<string>(() => this.node().id);

  /**
   * O runtime, quando existe.
   *
   * Opcional pelo mesmo motivo de sempre: uma tela sem regra nenhuma nao deve
   * pagar por ele, e o campo desenha igual.
   */
  private readonly runtime = inject(SCREEN_RUNTIME, { optional: true });

  /**
   * O campo convertido.
   *
   * `computed` memoiza por identidade: o objeto só é recriado quando o nó muda
   * de fato. Sem isso, o `ngOnChanges` do `webhook-common` dispararia a cada
   * ciclo de detecção — o padrão que já travou outras telas deste projeto.
   */
  readonly field = computed<WebhookField | null>(() => {
    const node = this.node();

    const converted = nodeToWebhookField(node, {
      translate: (key, params) => translateTAValue(this.translate, key, params),
      controlName: this.controlName()
    });

    const traduzido = withTranslatedText(
      converted,
      (key, params) => translateTAValue(this.translate, key, params)
    );

    return this.withRuntimeOptions(traduzido, node.id);
  });

  /**
   * As opcoes trocadas pela acao `setOptions`, quando houver.
   *
   * O estado do runtime VENCE as props: props sao a configuracao da tela, e a
   * acao existe justamente para trocar a lista sem alterar a configuracao. Sem
   * este passo, `setOptions` gravaria no estado e a tela continuaria mostrando
   * a lista antiga — a acao "funcionaria" sem efeito nenhum, que e o pior
   * desfecho possivel.
   */
  private withRuntimeOptions(field: WebhookField | null, nodeId: string): WebhookField | null {
    if (field === null || this.runtime === null) {
      return field;
    }

    const opcoes = this.runtime.stateOf(nodeId).options;

    if (opcoes === undefined) {
      return field;
    }

    return {
      ...field,
      options: opcoes.map(opcao => ({
        value: opcao.value,
        label: translateTAValue(this.translate, opcao.labelKey)
      }))
    } as WebhookField;
  }
}
